<script lang="ts">
  import Header from './Header.svelte';
  import Today from './Today/Today.svelte';
  import Calendar from './Calendar/Calendar.svelte';
  import ActivitiesDash from '../ActivitiesDash.svelte';
  import ProgramPage from '../Program/ProgramPage.svelte';
  import ProgressionPage from './Progression/ProgressionPage.svelte';
  import { Button } from '@/lib/components/ui/button';
  import { cn } from '@/lib/utils';
  import { onDestroy, tick } from 'svelte';
  import PredictiveModePrompt from '../PredictiveMode/PredictiveModePrompt.svelte';
  import { getPredictiveState, setPredictivePageUser, watchPredictiveState } from '../PredictiveMode/predictive';
  import type { XSSTargetsResponse } from '@/Util/api';

  // Props from blade
  // export let userParams;
  // export let trainingAdvice;
  // export let trainingPlan;
  // export let trickleActive;
  // export let cal_settings;
  // export let athlete_types;
  // export let training_medians;
  export let activity_types;
  export let likeScore;
  export let dislikeScore;
  export let program_weeks;
  export let challenge_days;
  export let workout_description;
  // export let workoutTypes;
  // export let workoutDifficultyOption;
  // export let workoutDurationOption;
  // export let focusTypesCycling;
  // export let focusTypesOther;
  // export let isCyclingProfile: boolean;
  export let maps_key: string = '';
  export let myFitness;
  export let first_name;
  export let last_name;
  export let profile_photo;
  export let premium;
  export let subscription;
  export let plan_icon;
  export let athlete;
  export let periods;
  export let last_breakthrough;
  export let username;
  export let sport_type;
  export let showStatsChart;
  export let user_timezone: string = '';
  /** Where the Progression tab reads from: the athlete's own data, or the athlete a coach or squad member is viewing. */
  export let progression_stats_url: string = '/my-fitness/activity_statistics';
  export let progression_measures_url: string = '/my-fitness/measures';

  // A red dot on Program while a breakthrough is waiting to be applied or put aside (answered on the Program tab).
  let breakthroughWaiting = false;
  setPredictivePageUser(username);
  getPredictiveState().then((state) => {
    breakthroughWaiting = !!state && state.enabled && !!state.pending;
  });
  // A breakthrough processed while the page is open puts the dot up without a reload.
  const stopWatching = typeof window !== 'undefined' ? watchPredictiveState((state) => (breakthroughWaiting = !!state && state.enabled && !!state.pending)) : () => {};
  onDestroy(stopWatching);
  const onBreakthroughAnswered = () => (breakthroughWaiting = false);
  if (typeof window !== 'undefined') window.addEventListener('xert-breakthrough-answered', onBreakthroughAnswered);

  const tabValues = ['training', 'planner', 'activities', 'progression', 'program'];
  const tabNames = ['Today', 'Planner', 'Activities', 'Progression', 'Program'];

  // Tabs that fill the viewport with their own content, where the profile header only pushes that
  // content down the page.
  const tabsHeaderVisibility: Record<string, string> = {
    'training': 'lg:hidden',
    'planner': 'hidden',
    'activities': 'hidden',
    'progression': 'hidden',
    'program': 'hidden'
  };

  // Default to 'planner' for initial release, switch to 'training' once tested.
  // export let activeTab = 'training';
  export let activeTab = 'planner';

  // For export in blade file
  export function openChangeProgram() {
    programPage?.openChangeProgram();
  }

  function scrollToTop() {
    const scroller: HTMLElement | Element | any = document.scrollingElement || document.documentElement;
    // Use requestAnimationFrame so we run after layout & sticky headers settle.
    requestAnimationFrame(() => {
      scroller.scrollTo({ top: 0, behavior: 'auto' });
    });
  }

  let programPage: ProgramPage
  async function onTabChange(value: string) {

    // Clicking the tab that is already active is not a tab change (and shouldn't warn about losing program edits).
    if (value === activeTab) return;

    // Program page may prevent tab change if editing program.
    if (activeTab == 'program' && programPage && programPage.beforeMyFitnessTabChange(value)) return;
    if (value === 'planner') {
      // Lock the background while planner (calendar) is active.
      document.body.style.setProperty('position', 'fixed', 'important');
      document.body.style.setProperty('width', '100vw', 'important');
      document.body.style.setProperty('height', '100vh', 'important');
      document.body.style.overflow = 'hidden';
    } else if (activeTab === 'planner') {
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.overflow = '';
    }
    activeTab = value;

    // Wait for the DOM to reflect the new tab
    await tick();
    scrollToTop();
  }

  let xssTargetsResponse: XSSTargetsResponse = null;

</script>

<div class={cn("flex flex-col")} on:myfitnesstabchange={(e) => onTabChange(e.detail.tabName)}>
  <PredictiveModePrompt {username} />
  <div class={cn(tabsHeaderVisibility[activeTab] ?? '')}>
    <Header {myFitness}
      last_breakthrough={last_breakthrough}
      first_name={first_name}
      last_name={last_name}
      profile_photo={profile_photo}
      premium={premium}
      subscription={subscription}
      plan_icon={plan_icon}
      athlete={athlete}
      workout_description={workout_description}
      periods={periods}
    />
  </div>
  <div class='w-full sticky top-[46px] z-10 flex flex-row bg-white border-0 border-b border-solid border-border'>
    {#each tabValues as tab, i}
      <Button variant='ghost' class={cn('flex-1 h-12 border-0 border-solid border-primary rounded-none text-gray-500 font-normal px-0', activeTab === tab ? 'border-b-2 text-primary bg-primary/10' : '')} on:click={() => onTabChange(tab)}>
        {tabNames[i]}{#if tab === 'program' && breakthroughWaiting}<span class="ml-1.5 inline-block h-2 w-2 rounded-full bg-red-600 align-middle" role="img" aria-label="A breakthrough is waiting for your answer" title="A breakthrough is waiting for your answer" />{/if}
      </Button>
    {/each}
  </div>
  <div class={cn('w-full p-2 md:p-4 bg-[#F9F9F9]', activeTab !== 'training' ? 'display-none' : '')}>
    <Today {athlete} lastBreakthrough={last_breakthrough} workoutDescription={workout_description} {sport_type} {activity_types} {likeScore} {dislikeScore} {program_weeks} {challenge_days} bind:xssTargetsResponse />
  </div>
  <div class={cn('w-full', activeTab !== 'planner' ? 'display-none' : '')}>
    <Calendar
      likeScore={likeScore}
      dislikeScore={dislikeScore}
      program_weeks={program_weeks}
      challenge_days={challenge_days}
      sport_type={sport_type}
      showStatsChart={showStatsChart}
      username={username}
      activity_types={activity_types}
      athlete={athlete}
      workout_description={workout_description}
      xssTargetsResponse={xssTargetsResponse}
    />
  </div>
  <div class={cn('w-full bg-[#F9F9F9]', activeTab !== 'activities' ? 'display-none' : '')}>
    <!-- The tab bar above is itself sticky at top-[46px] and is 49px tall (h-12 + border), so the
         dashboard's own sticky filter sidebar has to start below it. -->
    <ActivitiesDash dataSource="/activities/dashboard" stickyTop="95px" onScheduleActivity={() => onTabChange('planner')} />
  </div>
  <div class={cn('w-full bg-[#F9F9F9]', activeTab !== 'progression' ? 'display-none' : '')}>
    <!-- Same as the Activities tab: the sidebar sticks below the site header and this tab bar. -->
    <ProgressionPage {myFitness} stickyTop="95px" timezone={user_timezone} statsUrl={progression_stats_url} measuresUrl={progression_measures_url} />
  </div>
  <div class={cn('w-full p-2 md:p-4', activeTab !== 'program' ? 'display-none' : '')}>
    <ProgramPage bind:this={programPage} gmapsKey={maps_key} /> 
  </div>
</div>

<style>
  :global(.xert-svelte .tabs-trigger[data-state='active']) {
    font-weight: 600;
  }
</style>
